import { effect, signal } from '@preact/signals';

/**
 * Le thème — **clair, sombre, ou celui du système**, commutable et mémorisé.
 *
 * La V1 web savait le faire, la V2 ne le savait plus : elle suivait le système
 * sans offrir d'échappatoire. C'est un manque réel — une machine réglée en sombre
 * n'implique pas que *cette* application doive l'être.
 *
 * Le choix vit dans `localStorage`, à côté de la session mais sous une autre clé :
 * une déconnexion ne doit pas repasser l'écran en clair. C'est une préférence
 * d'appareil, pas une donnée de compte.
 */

export type Theme = 'light' | 'dark' | 'system';

const KEY = 'synkara.theme';

const stored = ((): Theme => {
  const raw = localStorage.getItem(KEY);
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
})();

export const theme = signal<Theme>(stored);

const dark = window.matchMedia('(prefers-color-scheme: dark)');

/** Ce que le système dit, **au moment où on le lui demande**. */
const systemTheme = signal<'light' | 'dark'>(dark.matches ? 'dark' : 'light');
dark.addEventListener('change', (ev) => {
  systemTheme.value = ev.matches ? 'dark' : 'light';
});

/**
 * Le thème effectivement appliqué. La CSS ne connaît que celui-là : elle n'a
 * qu'une règle `[data-theme='dark']` à porter, et pas une seconde copie de la
 * palette derrière une media query — deux copies divergent toujours.
 */
export function resolvedTheme(): 'light' | 'dark' {
  return theme.value === 'system' ? systemTheme.value : theme.value;
}

effect(() => {
  document.documentElement.dataset['theme'] = resolvedTheme();
  localStorage.setItem(KEY, theme.value);
});

/** Fait tourner le choix, dans l'ordre où on le comprend : système → clair → sombre. */
export function cycleTheme(): void {
  theme.value = theme.value === 'system' ? 'light' : theme.value === 'light' ? 'dark' : 'system';
}
